import { useMemo, useState } from 'react';
import { Analytics } from '@cvent/react-analytics';
import { usePageActions, usePageLoadMetrics } from '@cvent/sli-nextjs-metrics';
import analyticsDefinitionsArray from '@analytics/factDefinitions/getFactDefinitionsFromPlan';
import getConfig from 'next/config';
import { LoggerFactory } from '@cvent/logging/LoggerFactory';
import useAppStats from './useAppStats';

const LOG = LoggerFactory.create('useAnalytics');

const { publicRuntimeConfig } = getConfig();

const TIMESTAMP_OFFSET_THRESHOLD = 60000; // 1 minute
const ENGAGE_ACTION = 'engage_action';
const ENGAGE_PAGE_VIEW = 'engage_page_view';

export const analyticsBaseConfig =
  publicRuntimeConfig.ENABLE_ANALYTICS === 'true'
    ? {
        cventAnalytics: {
          url: publicRuntimeConfig.CVENT_ANALYTICS_URL,
          cventAnalyticsTypes: {
            action: ENGAGE_ACTION,
            pageView: ENGAGE_PAGE_VIEW
          }
        }
      }
    : {};

let analyticsInstance: Analytics = null;

/**
 * Initializes the Analytics instance if we haven't already
 */
export const setAnalyticsInstance = (analytics: Analytics) => {
  if (!analyticsInstance) {
    analyticsInstance = analytics;
  }
};

export const getAnalyticsInstance = (): Analytics => {
  return analyticsInstance;
};

/**
 * Hook to initialize Analytics. It puts the instance in the window object so it can be accessed
 * by the metricsReporter which is where we track actions for these destinations:
 *
 *  - Cvent Analytics
 *  - Attendee Activities
 *  - Mixpanel
 */

export function useInitAnalytics(baseProperties: Record<string, unknown>): void {
  const { updateMetricData } = usePageLoadMetrics();

  // If the client time is off server time by more than a minute, adjust
  // analytics fact timestamps accordingly.
  const appStats = useAppStats();
  const [clientFirstLoadTime] = useState(Date.now());
  const timestampOffset = new Date(appStats.firstLoadTime).getTime() - clientFirstLoadTime;
  const analyticsConfig = {
    ...analyticsBaseConfig,
    failIfMissingAttributes: false,
    pickOnlyAttributedProps: false,
    timestampOffset:
      timestampOffset > TIMESTAMP_OFFSET_THRESHOLD || timestampOffset < -TIMESTAMP_OFFSET_THRESHOLD
        ? timestampOffset
        : undefined
  };

  if (typeof window !== 'undefined' && !analyticsInstance && baseProperties) {
    LOG.debug('Initializing Analytics instance', analyticsConfig, baseProperties);
    // Add more baseProperties to PageActionsMetrics
    updateMetricData(baseProperties);
    setAnalyticsInstance(new Analytics(analyticsConfig, analyticsDefinitionsArray, baseProperties));
  }
}

/**
 * Hook that overrides the useAnalytics hook from react-analytics. Internally it uses the trackAction
 * function from the usePageActions API. This allows us to unified the way we track metrics in the application
 * for both SLI and Analytics purposes without breaking current functionality.
 *
 * @deprecated Migrate to the new CDP structure instead https://stash.cvent.net/projects/AX/repos/attendee-experience-web/browse/packages/attendee-experience-web/src/analytics/migrations/README.md
 */
export const useAnalytics = (): Analytics => {
  const { trackAction } = usePageActions();
  const { updateMetricData } = usePageLoadMetrics();
  const analytics = analyticsInstance || null;

  return useMemo(() => {
    if (!analytics) {
      return {
        // Avoid crashing if analyticsInstance is not yet initialized
        // useEffects will re-run once initialized as long as analytics is in the dependency array
        track: () => null
      };
    }

    return {
      ...analytics,
      updateBaseProperties: (data: Record<string, unknown>) => {
        analytics?.updateBaseProperties(data);
        updateMetricData(data);
      },
      track: (fact: Record<string, unknown>) => {
        // This is needed so we don't send everything to DD for analytics.track calls
        const definition = analytics?.getDefinitionByFact(fact) || {};
        const id = fact.id || definition?.id;

        if (!id) {
          return;
        }

        trackAction(id, {
          ...fact,
          ...(definition && { destinations: definition.destinations })
        });
      }
    };
  }, [trackAction, updateMetricData, analytics]);
};
